import React, { useState } from 'react';
import api from '../../../services/api';
import { FaClock, FaUser, FaFlag, FaEllipsisV, FaEdit, FaTrash } from 'react-icons/fa';

const TaskKanbanBoard = ({ tasks, statuses, priorities, users, onTaskClick, onTaskEdit, onRefresh }) => {
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, statusId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(statusId);
  };

  const handleDragLeave = (e) => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e, statusId) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (!draggedTask || draggedTask.status_id === statusId) {
      setDraggedTask(null);
      return;
    }

    try {
      await api.put(`/portal/tasks/${draggedTask.id}`, {
        ...draggedTask,
        status_id: statusId
      });

      onRefresh();
    } catch (error) {
      console.error('Error updating task status:', error);
      alert('Failed to update task status');
    }

    setDraggedTask(null);
  };

  const getTasksByStatus = (statusId) => {
    return tasks.filter(task => task.status_id === statusId);
  };

  const getPriorityColor = (priorityId) => {
    const priority = priorities.find(p => p.id === priorityId);
    return priority?.color || '#gray';
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return 'Unassigned';
    return user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.email;
  };

  const formatDueDate = (dueDate) => {
    if (!dueDate) return null;
    const date = new Date(dueDate);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { text: `${Math.abs(diffDays)}d overdue`, className: 'overdue' };
    if (diffDays === 0) return { text: 'Due today', className: 'today' };
    if (diffDays === 1) return { text: 'Due tomorrow', className: 'tomorrow' };
    if (diffDays <= 7) return { text: `Due in ${diffDays}d`, className: 'week' };
    return { text: `Due ${date.toLocaleDateString()}`, className: 'future' };
  };

  return (
    <div className="kanban-board">
      {statuses.map(status => {
        const statusTasks = getTasksByStatus(status.id);
        const isOver = dragOverColumn === status.id;

        return (
          <div
            key={status.id}
            className={`kanban-column ${isOver ? 'drag-over' : ''}`}
            onDragOver={(e) => handleDragOver(e, status.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, status.id)}
          >
            <div className="kanban-column-header" style={{ borderTopColor: status.color }}>
              <div className="kanban-column-title">
                <span className="status-indicator" style={{ backgroundColor: status.color }}></span>
                <h3>{status.name}</h3>
                <span className="task-count">{statusTasks.length}</span>
              </div>
            </div>

            <div className="kanban-column-content">
              {statusTasks.length === 0 ? (
                <div className="kanban-empty-state">
                  <p>No tasks</p>
                </div>
              ) : (
                statusTasks.map(task => {
                  const dueDateInfo = formatDueDate(task.due_date);

                  return (
                    <div
                      key={task.id}
                      className="kanban-task-card"
                      draggable
                      onDragStart={(e) => handleDragStart(e, task)}
                      onClick={() => onTaskClick(task)}
                    >
                      <div className="task-card-header">
                        <div
                          className="task-priority-indicator"
                          style={{ backgroundColor: getPriorityColor(task.priority_id) }}
                          title={priorities.find(p => p.id === task.priority_id)?.name}
                        ></div>
                        <h4 className="task-card-title">{task.title}</h4>
                      </div>

                      {task.description && (
                        <p className="task-card-description">
                          {task.description.length > 100
                            ? `${task.description.substring(0, 100)}...`
                            : task.description}
                        </p>
                      )}

                      <div className="task-card-meta">
                        {dueDateInfo && (
                          <div className={`task-due-date ${dueDateInfo.className}`}>
                            <FaClock /> {dueDateInfo.text}
                          </div>
                        )}

                        {task.assigned_to && (
                          <div className="task-assignee">
                            <FaUser /> {getUserName(task.assigned_to).split(' ')[0]}
                          </div>
                        )}
                      </div>

                      {task.tags && task.tags.length > 0 && (
                        <div className="task-card-tags">
                          {task.tags.slice(0, 3).map((tag, idx) => (
                            <span
                              key={idx}
                              className="task-tag"
                              style={{ backgroundColor: tag.color + '20', color: tag.color }}
                            >
                              {tag.name}
                            </span>
                          ))}
                          {task.tags.length > 3 && (
                            <span className="task-tag-more">+{task.tags.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TaskKanbanBoard;
